import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { FileDrop } from "./FileDrop";
import { euro } from "@/content/financing";
import { trackConversion } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INITIAL = { nome: "", cognome: "", telefono: "", email: "", note: "", consenso: false };
const inputCls = "w-full bg-transparent border-0 border-b border-[#1B3B28]/20 px-0 py-3 text-lg text-[#1B3B28] placeholder:text-[#526356]/50 focus:outline-none focus:border-[#3E6B48] transition-colors";

const Field = ({ label, children }) => (
  <label className="block"><span className="eyebrow text-[#526356]">{label}</span>{children}</label>
);

export const QuoteForm = ({ amount, rate, onDone }) => {
  const [form, setForm] = useState(INITIAL);
  const [files, setFiles] = useState({ preventivo: null, panoramica: null });
  const [progress, setProgress] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setFile = (k) => (v) => setFiles((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!files.preventivo) return toast.error("Carica la foto o il PDF del tuo preventivo.");
    if (!form.consenso) return toast.error("Serve il consenso al trattamento dei dati per valutare il preventivo.");
    const fd = new FormData();
    Object.entries({ ...form, importo: amount, rate }).forEach(([k, v]) => fd.append(k, v));
    fd.append("preventivo", files.preventivo);
    if (files.panoramica) fd.append("panoramica", files.panoramica);
    setProgress(0);
    try {
      const { data } = await axios.post(`${API}/quotes`, fd, { onUploadProgress: (p) => setProgress(Math.round((p.loaded / (p.total || 1)) * 100)) });
      trackConversion("preventivo_caricato", { importo: amount, rate, con_panoramica: files.panoramica ? "si" : "no" });
      onDone({ name: form.nome, email: form.email, emailSent: data?.patient_email_sent });
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Invio non riuscito. Riprova o scrivici su WhatsApp.");
    } finally {
      setProgress(null);
    }
  };

  const busy = progress !== null;
  return (
    <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-8" data-testid="quote-form">
      <div className="sm:col-span-2 rounded-2xl bg-[#E6ECE4] px-5 py-4 flex flex-wrap items-baseline justify-between gap-2" data-testid="quote-summary">
        <span className="text-sm text-[#526356]">La tua simulazione</span>
        <span className="f-serif text-xl text-[#1B3B28]">{euro(amount)} in {rate} rate da {euro(amount / rate)}</span>
      </div>
      <Field label="Nome"><input required maxLength={60} value={form.nome} onChange={set("nome")} className={inputCls} placeholder="Maria" data-testid="quote-input-nome" /></Field>
      <Field label="Cognome"><input required maxLength={60} value={form.cognome} onChange={set("cognome")} className={inputCls} placeholder="Rossi" data-testid="quote-input-cognome" /></Field>
      <Field label="Telefono"><input required type="tel" minLength={6} maxLength={30} value={form.telefono} onChange={set("telefono")} className={inputCls} placeholder="+39 ..." data-testid="quote-input-telefono" /></Field>
      <Field label="Email"><input required type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="nome@email.it" data-testid="quote-input-email" /></Field>
      <FileDrop id="preventivo" label="Il tuo preventivo" hint="Foto o PDF, massimo 10 MB" required file={files.preventivo} onChange={setFile("preventivo")} onError={toast.error} />
      <FileDrop id="panoramica" label="Foto della panoramica" hint="Se ce l'hai: foto o PDF, massimo 10 MB" file={files.panoramica} onChange={setFile("panoramica")} onError={toast.error} />
      <div className="sm:col-span-2">
        <Field label="Note (facoltative)">
          <textarea rows={2} maxLength={1000} value={form.note} onChange={set("note")} className={`${inputCls} resize-none`} placeholder="Dubbi, sintomi o domande sul piano di cura" data-testid="quote-input-note" />
        </Field>
      </div>
      <div className="sm:col-span-2 -mt-2 flex items-start gap-3">
        <Checkbox id="quote-consenso" checked={form.consenso} onCheckedChange={(v) => setForm((f) => ({ ...f, consenso: !!v }))} data-testid="quote-consent-checkbox" className="mt-0.5 h-5 w-5" />
        <label htmlFor="quote-consenso" className="text-sm text-[#526356]">
          Acconsento al trattamento dei miei dati, inclusi quelli relativi alla salute contenuti nei file caricati, per ricevere una valutazione e una proposta, come descritto nella{" "}
          <a href="https://www.iubenda.com/privacy-policy/67116616" target="_blank" rel="noopener noreferrer" className="iubenda-nostyle text-[#1B3B28] underline underline-offset-2" data-testid="quote-privacy-link">Privacy Policy</a>.
        </label>
      </div>
      <div className="sm:col-span-2">
        <button type="submit" disabled={busy} data-testid="quote-submit-btn"
          className="group inline-flex items-center gap-3 rounded-full bg-[#1B3B28] text-[#FCFBF7] pl-8 pr-2 py-2 hover:bg-[#3E6B48] transition-colors disabled:opacity-60">
          {busy ? (progress < 100 ? `Caricamento ${progress}%` : "Invio in corso") : "Invia il preventivo"}
          <span className="grid place-items-center w-11 h-11 rounded-full bg-[#86C89B] text-[#0F2317] transition-transform duration-500 group-hover:translate-x-1">
            {busy ? <Loader2 size={18} className="animate-spin" /> : <ArrowRight size={18} />}
          </span>
        </button>
      </div>
    </form>
  );
};
